import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Cookies from "universal-cookie";

const MOBILE_MQ = "(max-width: 992px)";
const MAX_SCALE = 4;
const ACCOUNT_WIDTH = 1350;
const PAN_SPEED = 2.1;
const PAN_FRICTION = 0.92;
const REFRESH_THRESHOLD = 70;

export const isMobileView = () =>
  typeof window !== "undefined" && window.matchMedia(MOBILE_MQ).matches;

export const isAccountPath = (pathname: string) => {
  const path = pathname.toLowerCase();
  return path.startsWith("/user") || path.startsWith("/profile");
};

export const useIsMobileView = () => {
  const [mobile, setMobile] = useState(isMobileView);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onMq = () => setMobile(mq.matches);
    mq.addEventListener("change", onMq);
    return () => mq.removeEventListener("change", onMq);
  }, []);

  return mobile;
};

const touchDist = (
  a: { clientX: number; clientY: number },
  b: { clientX: number; clientY: number }
) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

const touchMid = (
  a: { clientX: number; clientY: number },
  b: { clientX: number; clientY: number }
) => ({
  x: (a.clientX + b.clientX) / 2,
  y: (a.clientY + b.clientY) / 2,
});

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const ForuserPortal = ({ children }: { children: React.ReactNode }) => {
  const mobile = useIsMobileView();
  if (mobile) return null;
  return <>{children}</>;
};

type ZoomablePageContentProps = {
  children: React.ReactNode;
};

const ZoomablePageContent = ({ children }: ZoomablePageContentProps) => {
  const location = useLocation();
  const cookies = useRef(new Cookies());
  const shellRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const scaleRef = useRef(1);
  const txRef = useRef(0);
  const tyRef = useRef(0);
  const contentSize = useRef({ w: ACCOUNT_WIDTH, h: 1 });
  const viewSize = useRef({ w: 1, h: 1, left: 0, top: 0 });
  const rafRef = useRef(0);
  const userZoomed = useRef(false);
  const pinchRef = useRef<{
    startDist: number;
    startScale: number;
  } | null>(null);
  const panRef = useRef<{
    x: number;
    y: number;
    tx: number;
    ty: number;
    moved: boolean;
    lastT: number;
    vx: number;
    vy: number;
  } | null>(null);
  const inertiaRaf = useRef(0);
  const pullRef = useRef({ active: false, dist: 0 });
  const hintRef = useRef<HTMLDivElement | null>(null);
  const [mobile, setMobile] = useState(isMobileView);
  const betHistoryShow = useSelector((e: any) => !!e.betHistoryShow);
  const isMaintenance = useSelector(
    (e: any) => e.domainDetails?.isMaintenance === true,
  );

  const path = location.pathname.toLowerCase();
  const authToken = cookies.current.get("skyTokenFront");
  const showHeader =
    !isMaintenance &&
    path !== "/maintenance" &&
    ((path !== "/login" && path !== "/change/password") || !!authToken);
  const accountView = isAccountPath(location.pathname);
  const useFrame = mobile && !!showHeader && accountView;

  const applyTransform = useCallback(() => {
    const inner = innerRef.current;
    if (!inner) return;
    inner.style.transform = `translate3d(${txRef.current}px, ${tyRef.current}px, 0) scale3d(${scaleRef.current}, ${scaleRef.current}, 1)`;
  }, []);

  const scheduleTransform = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = 0;
      applyTransform();
    });
  }, [applyTransform]);

  const cacheView = useCallback(() => {
    const shell = shellRef.current;
    if (!shell) return;
    const rect = shell.getBoundingClientRect();
    viewSize.current = {
      w: rect.width,
      h: rect.height,
      left: rect.left,
      top: rect.top,
    };
  }, []);

  const measureContent = useCallback(() => {
    const inner = innerRef.current;
    if (!inner) return;
    let w = Math.max(inner.scrollWidth, inner.offsetWidth, ACCOUNT_WIDTH, 1);
    let h = 1;
    const bump = (el: HTMLElement | null) => {
      if (!el) return;
      w = Math.max(w, el.scrollWidth, el.offsetWidth);
      h = Math.max(h, el.scrollHeight, el.offsetHeight);
    };
    Array.from(inner.children).forEach((node) => bump(node as HTMLElement));
    bump(inner.querySelector(".account") as HTMLElement | null);
    bump(inner.querySelector(".account_tabs_r") as HTMLElement | null);
    bump(inner.querySelector(".my-bets-page") as HTMLElement | null);
    bump(inner.querySelector(".my-bets-table") as HTMLElement | null);
    contentSize.current = { w, h: Math.max(h, 1) };
  }, []);

  const clampPan = useCallback(() => {
    const sw = viewSize.current.w;
    const sh = viewSize.current.h;
    const cw = contentSize.current.w * scaleRef.current;
    const ch = contentSize.current.h * scaleRef.current;
    txRef.current = cw <= sw ? 0 : clamp(txRef.current, sw - cw, 0);
    tyRef.current = ch <= sh ? 0 : clamp(tyRef.current, sh - ch, 0);
  }, []);

  const getFitScale = useCallback(() => {
    const width = viewSize.current.w || shellRef.current?.clientWidth || window.innerWidth;
    if (!width) return 0.3;
    return clamp(width / ACCOUNT_WIDTH, 0.15, 1);
  }, []);

  const setScaleAround = useCallback(
    (nextScale: number, originX: number, originY: number) => {
      const prev = scaleRef.current || 1;
      const scale = clamp(nextScale, getFitScale(), MAX_SCALE);
      const mx = originX - viewSize.current.left;
      const my = originY - viewSize.current.top;
      const ratio = scale / prev;
      txRef.current = mx - (mx - txRef.current) * ratio;
      tyRef.current = my - (my - tyRef.current) * ratio;
      scaleRef.current = scale;
      userZoomed.current = true;
      clampPan();
      scheduleTransform();
    },
    [clampPan, getFitScale, scheduleTransform]
  );

  const resetToFit = useCallback(() => {
    cacheView();
    measureContent();
    scaleRef.current = getFitScale();
    txRef.current = 0;
    tyRef.current = 0;
    userZoomed.current = false;
    clampPan();
    applyTransform();
  }, [applyTransform, cacheView, clampPan, getFitScale, measureContent]);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onMq = () => setMobile(mq.matches);
    mq.addEventListener("change", onMq);
    return () => mq.removeEventListener("change", onMq);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("mobile-zoom-frame", useFrame);
    document.body.classList.toggle("is-account-webview", useFrame && accountView);
    return () => {
      document.body.classList.remove("mobile-zoom-frame");
      document.body.classList.remove("is-account-webview");
    };
  }, [useFrame, accountView]);

  useEffect(() => {
    if (!useFrame) return;
    const id = window.requestAnimationFrame(() => resetToFit());
    const t = window.setTimeout(() => {
      if (!userZoomed.current) resetToFit();
    }, 200);
    const onResize = () => {
      if (!userZoomed.current) resetToFit();
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.cancelAnimationFrame(id);
      window.clearTimeout(t);
      window.removeEventListener("resize", onResize);
    };
  }, [useFrame, location.pathname, resetToFit]);

  useEffect(() => {
    if (!useFrame || betHistoryShow) return;
    const shell = shellRef.current;
    if (!shell) return;

    const preventGesture = (e: Event) => e.preventDefault();
    shell.addEventListener("gesturestart", preventGesture, { passive: false });
    shell.addEventListener("gesturechange", preventGesture, { passive: false });

    const stopInertia = () => {
      if (inertiaRaf.current) {
        window.cancelAnimationFrame(inertiaRaf.current);
        inertiaRaf.current = 0;
      }
    };

    const runInertia = (vx: number, vy: number) => {
      stopInertia();
      const step = () => {
        vx *= PAN_FRICTION;
        vy *= PAN_FRICTION;
        if (Math.hypot(vx, vy) < 0.08) {
          inertiaRaf.current = 0;
          applyTransform();
          return;
        }
        txRef.current += vx * 16;
        tyRef.current += vy * 16;
        clampPan();
        applyTransform();
        inertiaRaf.current = window.requestAnimationFrame(step);
      };
      inertiaRaf.current = window.requestAnimationFrame(step);
    };

    const setHint = (dist: number, ready: boolean) => {
      const hint = hintRef.current;
      if (!hint) return;
      hint.classList.toggle("is-visible", dist > 8);
      hint.classList.toggle("is-ready", ready);
      hint.textContent = ready ? "Release to refresh" : "Pull to refresh";
    };

    const onStart = (e: TouchEvent) => {
      stopInertia();
      pullRef.current = { active: false, dist: 0 };
      cacheView();
      measureContent();
      if (e.touches.length >= 2) {
        pinchRef.current = {
          startDist: touchDist(e.touches[0], e.touches[1]),
          startScale: scaleRef.current,
        };
        panRef.current = null;
        return;
      }
      if (e.touches.length === 1) {
        const t = e.touches[0];
        panRef.current = {
          x: t.clientX,
          y: t.clientY,
          tx: txRef.current,
          ty: tyRef.current,
          moved: false,
          lastT: performance.now(),
          vx: 0,
          vy: 0,
        };
      }
    };

    const onMove = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        e.preventDefault();
        stopInertia();
        pullRef.current.active = false;
        setHint(0, false);
        const dist = touchDist(e.touches[0], e.touches[1]);
        const mid = touchMid(e.touches[0], e.touches[1]);
        if (!pinchRef.current) {
          pinchRef.current = {
            startDist: dist,
            startScale: scaleRef.current,
          };
        }
        const next =
          pinchRef.current.startScale *
          (dist / Math.max(pinchRef.current.startDist, 8));
        setScaleAround(next, mid.x, mid.y);
        panRef.current = null;
        return;
      }
      if (e.touches.length === 1 && panRef.current && !pinchRef.current) {
        const start = panRef.current;
        const fingerDx = e.touches[0].clientX - start.x;
        const fingerDy = e.touches[0].clientY - start.y;
        const atTop = start.ty >= -1 && tyRef.current >= -1;
        const pullingDown =
          atTop &&
          fingerDy > 10 &&
          fingerDy > Math.abs(fingerDx) * 1.2;

        if (pullRef.current.active || pullingDown) {
          e.preventDefault();
          pullRef.current.active = true;
          pullRef.current.dist = Math.min(120, Math.max(0, fingerDy * 0.45));
          tyRef.current = pullRef.current.dist;
          setHint(pullRef.current.dist, pullRef.current.dist >= REFRESH_THRESHOLD);
          scheduleTransform();
          return;
        }

        const cw = contentSize.current.w * scaleRef.current;
        const ch = contentSize.current.h * scaleRef.current;
        const canPan =
          cw > viewSize.current.w + 2 || ch > viewSize.current.h + 2;
        if (!canPan) return;
        const now = performance.now();
        const stepDx = (e.touches[0].clientX - start.x) * PAN_SPEED;
        const stepDy = (e.touches[0].clientY - start.y) * PAN_SPEED;
        if (Math.hypot(fingerDx, fingerDy) > 2) start.moved = true;
        if (start.moved) {
          e.preventDefault();
          const dt = Math.max(now - start.lastT, 8);
          start.vx = stepDx / dt;
          start.vy = stepDy / dt;
          start.lastT = now;
          start.x = e.touches[0].clientX;
          start.y = e.touches[0].clientY;
          start.tx = txRef.current;
          start.ty = tyRef.current;
          txRef.current += stepDx;
          tyRef.current += stepDy;
          clampPan();
          scheduleTransform();
        }
      }
    };

    const onEnd = (e: TouchEvent) => {
      if (e.touches.length >= 2) return;
      const didPinch = !!pinchRef.current;
      pinchRef.current = null;
      if (pullRef.current.active) {
        const shouldReload = pullRef.current.dist >= REFRESH_THRESHOLD;
        pullRef.current.active = false;
        pullRef.current.dist = 0;
        setHint(0, false);
        panRef.current = null;
        if (shouldReload) {
          window.location.reload();
          return;
        }
        tyRef.current = 0;
        clampPan();
        applyTransform();
        return;
      }
      const pan = panRef.current;
      if (e.touches.length === 0) {
        if (pan?.moved && performance.now() - pan.lastT < 80) {
          runInertia(pan.vx, pan.vy);
        }
        panRef.current = null;
      }
      if (didPinch) {
        const fit = getFitScale();
        if (scaleRef.current <= fit * 1.04) {
          scaleRef.current = fit;
          txRef.current = 0;
          tyRef.current = 0;
          applyTransform();
        }
      }
    };

    shell.addEventListener("touchstart", onStart, { passive: true });
    shell.addEventListener("touchmove", onMove, { passive: false });
    shell.addEventListener("touchend", onEnd);
    shell.addEventListener("touchcancel", onEnd);
    return () => {
      stopInertia();
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      shell.removeEventListener("gesturestart", preventGesture);
      shell.removeEventListener("gesturechange", preventGesture);
      shell.removeEventListener("touchstart", onStart);
      shell.removeEventListener("touchmove", onMove);
      shell.removeEventListener("touchend", onEnd);
      shell.removeEventListener("touchcancel", onEnd);
    };
  }, [
    useFrame,
    applyTransform,
    cacheView,
    clampPan,
    measureContent,
    scheduleTransform,
    setScaleAround,
    getFitScale,
    betHistoryShow,
  ]);

  if (!useFrame) {
    return <>{children}</>;
  }

  return (
    <div
      ref={shellRef}
      className={`app-zoom-shell${accountView ? " is-account" : ""}`}
    >
      <div ref={hintRef} className="app-zoom-refresh">
        Pull to refresh
      </div>
      <div ref={innerRef} className="app-zoom-inner">
        {children}
      </div>
    </div>
  );
};

export default ZoomablePageContent;
